import 'dayjs/locale/vi';
import 'reflect-metadata';

import { CssBaseline } from '@mui/material';
import { LocalizationProvider, type PickersLocaleText } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { viVN } from '@mui/x-date-pickers/locales';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import type { Dayjs } from 'dayjs';
import * as ReactDOM from 'react-dom/client';
import { ThemeRegistry } from './config/theme/index.ts';
import ToastContainer from './shared/components/toast-container/toast-container.tsx';
import { initLocaleForDayJs } from './shared/utilities/dayjs-local.ts';
import { MountPoint } from './shared/utilities/sync-ui.ts';

import 'react-photo-view/dist/react-photo-view.css';
import 'react-toastify/dist/ReactToastify.css';
import { routeTree } from './routeTree.gen.ts';
import { queryClient } from './shared/queries/query-client.ts';
import './tailwind.css';
import './config/firebase-config.ts';
import './i18n.ts';

// Register Firebase Messaging service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(import.meta.env.PROD ? '/js/chat/firebase-messaging-sw.js' : '/firebase-messaging-sw.js')
      .then(() => console.info('[SW] Firebase messaging service worker registered'))
      .catch((err) => console.error('[SW] Registration failed', err));
  });
}

initLocaleForDayJs();
const localeText: Partial<PickersLocaleText<Dayjs>> = {
  ...viVN.components.MuiLocalizationProvider.defaultProps.localeText,
  okButtonLabel: 'Chọn'
};

const router = createRouter({
  routeTree,
  context: {
    queryClient
  },
  basepath: '/',
  defaultPreload: 'render',
  defaultPreloadStaleTime: 0
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// HMR-safe root creation - use global to persist across HMR cycles
declare global {
  interface Window {
    __reactRoot?: ReactDOM.Root;
  }
}

const rootElement = document.getElementById('chat-root');

if (!rootElement) {
  throw new Error('Root element "chat-root" not found');
}

// Create or reuse root instance using global to persist across HMR
let root = window.__reactRoot;
if (!root) {
  root = ReactDOM.createRoot(rootElement);
  window.__reactRoot = root;
}

root.render(
  <QueryClientProvider client={queryClient}>
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='vi' localeText={localeText}>
      <ThemeRegistry>
        <CssBaseline />
        <RouterProvider router={router} />
        <MountPoint />
        <ToastContainer autoClose={3000} hideProgressBar limit={3} />
      </ThemeRegistry>
    </LocalizationProvider>
  </QueryClientProvider>
);

// HMR cleanup for root (keep)
if (import.meta.hot) {
  import.meta.hot.accept();
  import.meta.hot.dispose(() => {
    root?.unmount();
    // Don't null the global root to prevent recreation during HMR
    // Only unmount, the root will be reused on next render
  });
}
